import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonIcon from "@mui/icons-material/Person";
import RefreshIcon from "@mui/icons-material/Refresh";

const ChatIPUsersSection = ({ ipAddress }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchIPData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ipAddress]);

  const fetchIPData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/ip-addresses/by-ip/${encodeURIComponent(ipAddress)}`
      );
      setData(response.data);
    } catch (err) {
      console.error("Failed to fetch IP data:", err);
      setError(err.response?.data?.error || "Failed to load IP data");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    return new Date(dateStr).toLocaleString();
  };

  const getGenderColor = (gender) => {
    switch (gender) {
      case "male": return "#2196f3";
      case "female": return "#e91e63";
      case "other": return "#9c27b0";
      default: return "#9e9e9e";
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress sx={{ color: "#fff" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/admin/chatroom/ip")}
          sx={{ color: "#9e9e9e", mb: 2 }}
        >
          Back to IP Addresses
        </Button>
        <Paper sx={{ p: 3, backgroundColor: "#16213e", border: "1px solid #394867" }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      </Box>
    );
  }

  const { ipAddress: ipData, guests } = data;

  return (
    <Box>
      {/* Header with back button */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/admin/chatroom/ip")}
          sx={{ color: "#9e9e9e", mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h5" sx={{ color: "#fff" }}>
          Users from IP: {ipAddress}
        </Typography>
        <IconButton
          onClick={fetchIPData}
          disabled={loading}
          sx={{
            ml: 2,
            color: "#9e9e9e",
            "&:hover": { color: "#fff", backgroundColor: "#394867" },
          }}
          title="Refresh"
        >
          <RefreshIcon
            sx={{
              animation: loading ? "spin 1s linear infinite" : "none",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          />
        </IconButton>
        {ipData.is_banned && (
          <Chip
            label="BANNED"
            size="small"
            sx={{ ml: 2, backgroundColor: "#f44336", color: "#fff" }}
          />
        )}
      </Box>

      {/* IP Info Card */}
      <Paper sx={{ p: 3, mb: 3, backgroundColor: "#16213e", border: "1px solid #394867" }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" sx={{ color: "#9e9e9e" }}>Location</Typography>
            <Typography sx={{ color: "#fff" }}>
              {[ipData.city, ipData.region, ipData.country].filter(Boolean).join(", ") || "Unknown"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" sx={{ color: "#9e9e9e" }}>ISP</Typography>
            <Typography sx={{ color: "#fff" }}>{ipData.isp || "Unknown"}</Typography>
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography variant="body2" sx={{ color: "#9e9e9e" }}>Total Guests</Typography>
            <Typography sx={{ color: "#fff", fontWeight: 600 }}>{guests.length}</Typography>
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography variant="body2" sx={{ color: "#9e9e9e" }}>First Seen</Typography>
            <Typography sx={{ color: "#fff" }}>{formatDate(ipData.first_seen_at)}</Typography>
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography variant="body2" sx={{ color: "#9e9e9e" }}>Last Seen</Typography>
            <Typography sx={{ color: "#fff" }}>{formatDate(ipData.last_seen_at)}</Typography>
          </Grid>
        </Grid>
        {ipData.is_banned && ipData.ban_reason && (
          <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #394867" }}>
            <Typography variant="body2" sx={{ color: "#f44336" }}>
              Ban Reason: {ipData.ban_reason}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Guests Table */}
      <Paper sx={{ backgroundColor: "#16213e", border: "1px solid #394867" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>Username</TableCell>
                <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>Gender</TableCell>
                <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>Birth Year</TableCell>
                <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>Messages</TableCell>
                <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>Status</TableCell>
                <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>Created</TableCell>
                <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>Last Seen</TableCell>
                <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {guests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ color: "#666", textAlign: "center", borderBottom: "1px solid #394867" }}>
                    No guests associated with this IP address
                  </TableCell>
                </TableRow>
              ) : (
                guests.map((guest) => (
                  <TableRow key={guest.id} hover sx={{ "&:hover": { backgroundColor: "#1a1a2e" } }}>
                    <TableCell sx={{ color: "#fff", borderBottom: "1px solid #394867" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <PersonIcon sx={{ color: getGenderColor(guest.gender), fontSize: 20 }} />
                        {guest.username}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid #394867" }}>
                      <Chip
                        label={guest.gender || "Unknown"}
                        size="small"
                        sx={{
                          backgroundColor: `${getGenderColor(guest.gender)}22`,
                          color: getGenderColor(guest.gender),
                          textTransform: "capitalize",
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: "#fff", borderBottom: "1px solid #394867" }}>
                      {guest.birth_year || "-"}
                    </TableCell>
                    <TableCell sx={{ color: "#fff", borderBottom: "1px solid #394867" }}>
                      {guest.message_count || 0}
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid #394867" }}>
                      {guest.is_banned ? (
                        <Tooltip title={guest.ban_reason || "Banned"}>
                          <Chip
                            icon={<BlockIcon sx={{ fontSize: 14 }} />}
                            label="Banned"
                            size="small"
                            sx={{ backgroundColor: "#f4433622", color: "#f44336" }}
                          />
                        </Tooltip>
                      ) : (
                        <Chip
                          icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                          label="Active"
                          size="small"
                          sx={{ backgroundColor: "#4caf5022", color: "#4caf50" }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867", fontSize: "0.85rem" }}>
                      {formatDate(guest.created_at)}
                    </TableCell>
                    <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867", fontSize: "0.85rem" }}>
                      {formatDate(guest.last_seen_at)}
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid #394867" }}>
                      <Tooltip title="View User Details">
                        <IconButton
                          component={Link}
                          to={`/admin/chatroom/user/${encodeURIComponent(guest.username)}`}
                          size="small"
                          sx={{ color: "#2196f3" }}
                        >
                          <PersonIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default ChatIPUsersSection;
